import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from "yup";
import { Typography, Box, alpha } from '@mui/material';
import { useDispatch } from 'react-redux';
import toast from 'src/utils/toast';
import useResponsive from 'src/components/hooks/useResponsive';
import InputComponent from 'src/components/shared/Form/Input';
import ButtonComponent from 'src/components/shared/Button';
import theme, { palette, shadows, typography } from 'src/config/theme';
import { useNavigate } from 'react-router-dom';

import { login, postOtpRequest, postOtpWithLoginRequest } from '../store/authActions';

function LoginForm() {
  const dispatch = useDispatch();
  const { isDesktop } = useResponsive();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [isOtpLogin, setIsOtpLogin] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const handleSubmit = (data, { resetForm }) => {
    return new Promise((resolve, reject) => {
      setLoading(true);

      if (isOtpLogin) {
        if (!otpSent) {
          // Step 1: Send OTP
          dispatch(postOtpRequest(
            { contactNo: data.contactNo },
            () => {
              toast.success("OTP sent to your Whatsapp");
              setOtpSent(true);
              resolve();
            },
            (err) => {
              toast.error(err?.message || "Failed to send OTP");
              setLoading(false);
              reject(err);
            }
          )
          );
        } else {
          // Step 2: Verify OTP & Login
          dispatch(
            postOtpWithLoginRequest(
              { contactNo: data.contactNo, otp: Number(data.otp) },
              () => {
                toast.success("Login Successful");
                setLoading(false);
                resolve();
                resetForm();
                setOtpSent(false);
              },
              (err) => {
                toast.error(err?.message || "OTP verification failed");
                setLoading(false);
                reject(err);
              }
            )
          );
        }
      } else {
        // Normal email/password login
        dispatch(
          login(
            data,
            () => {
              toast.success("Login Successful");
              // Navigation is handled by restoreSession saga
              setLoading(false);
              resolve();
            },
            (err) => {
              toast.error(err?.message || "Login failed");
              setLoading(false);
              reject(err);
            }
          )
        );
      }
    });
  };

  const validationSchema = Yup.object().shape({
    ...(isOtpLogin
      ? {
        contactNo: Yup.string().required("Phone is required"),
        ...(otpSent ? { otp: Yup.string().required("OTP is required") } : {}),
      }
      : {
        email: Yup.string().email("Invalid email").required("Email is required"),
        password: Yup.string().required("Password is required"),
      }),
  });

  return (
    <Formik
      initialValues={{
        email: '',
        password: '',
        contactNo: '',
        otp: '',
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit: formikSubmit,
        resetForm,
      }) => (
        <Form onSubmit={formikSubmit}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              width: { xs: '100%', sm: '340px' },
              mx: 'auto',
            }}
          >
            {/* Phone or Email input */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', height: '76px' }}>
              <Typography sx={{ ...typography.smallRegular, color: theme.palette.gery.black }}>
                {isOtpLogin ? 'Phone' : 'Email'}
              </Typography>
              <InputComponent
                variant="outlined"
                name={isOtpLogin ? 'contactNo' : 'email'}
                fullWidth
                placeholder={isOtpLogin ? 'Enter Phone Number' : 'Enter Email'}
                value={isOtpLogin ? values.contactNo : values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  isOtpLogin
                    ? touched.contactNo && Boolean(errors.contactNo)
                    : touched.email && Boolean(errors.email)
                }
                helperText={
                  isOtpLogin ? (touched.contactNo && errors.contactNo) : (touched.email && errors.email)
                }
                sx={{
                  backgroundColor: palette.gery.white,
                  boxShadow: shadows[1],
                  '& .MuiOutlinedInput-root': {
                    padding: '10px 0px',
                    borderRadius: '12px',
                    height: '52px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: `1px solid ${alpha(palette.gery.black, 0.14)}`,
                  },
                }}
              />
            </Box>

            {/* Password input for email login */}
            {!isOtpLogin && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', height: '76px' }}>
                <Typography sx={{ ...typography.smallRegular, color: theme.palette.gery.black }}>
                  Password
                </Typography>
                <InputComponent
                  variant="outlined"
                  name="password"
                  placeholder="Enter Password"
                  fullWidth
                  type="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  sx={{
                    backgroundColor: palette.gery.white,
                    boxShadow: shadows[1],
                    '& .MuiOutlinedInput-root': {
                      padding: '10px 0px',
                      borderRadius: '12px',
                      height: '52px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: `1px solid ${alpha(palette.gery.black, 0.14)}`,
                    },
                  }}
                />
              </Box>
            )}

            {/* OTP input for OTP login */}
            {isOtpLogin && otpSent && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', height: '76px' }}>
                <Typography sx={{ ...typography.smallRegular, color: theme.palette.gery.black }}>
                  OTP
                </Typography>
                <InputComponent
                  variant="outlined"
                  name="otp"
                  placeholder="Enter OTP"
                  fullWidth
                  value={values.otp}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.otp && Boolean(errors.otp)}
                  helperText={touched.otp && errors.otp}
                  sx={{
                    backgroundColor: palette.gery.white,
                    boxShadow: shadows[1],
                    '& .MuiOutlinedInput-root': {
                      padding: '10px 0px',
                      borderRadius: '12px',
                      height: '52px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: `1px solid ${alpha(palette.gery.black, 0.14)}`,
                    },
                  }}
                />
              </Box>
            )}

            {/* Toggle login method */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Typography
                variant="small"
                sx={{
                  color: theme.palette.primary.main,
                  borderBottom: `1px solid ${theme.palette.primary.main}`,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setIsOtpLogin(!isOtpLogin);
                  setOtpSent(false);
                  resetForm();
                }}
              >
                {isOtpLogin ? 'Login with Password' : 'Login With OTP'}
              </Typography>
            </Box>

            {/* Submit button */}
            <ButtonComponent
              variant="contained"
              fullWidth
              type="submit"
              sx={{
                padding: '10px 0px',
                borderRadius: '12px',
                height: '52px',
              }}
            >
              {isOtpLogin ? (otpSent ? 'Verify OTP' : 'Send OTP') : 'Log In'}
            </ButtonComponent>
          </Box>
        </Form>
      )}
    </Formik>
  );
}

export default LoginForm;
